import React, { ReactNode } from "react";

import { getApiUrl } from "@/config";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import { Poll, PollResult, PollVote } from "@/service/Model";
import TrackingService from "@/service/Tracking";

export type PollProviderProps = {
  interaction: Poll;
  preview: boolean;
  children?: ReactNode;
};

export type PollProviderState = {
  votes: PollVote[];
  selection: PollVote[];
  isPreview: boolean;
  onVote: (vote: PollVote) => void;
  onSelect: (vote: PollVote) => void;
  onConfirmSelection: () => void;
  hasVotes: () => boolean;
  isSlideVoted: (slideId: string) => boolean;
};

const PollContext = React.createContext<PollProviderState>({
  hasVotes: () => false,
  isPreview: false,
  isSlideVoted: () => false,
  onConfirmSelection: () => null,
  onSelect: () => null,
  onVote: () => null,
  selection: [],
  votes: [],
});

export class PollProvider extends React.Component<
  PollProviderProps,
  PollProviderState
> {
  private api: InteractionApi;

  public constructor(props: PollProviderProps) {
    super(props);

    let votes: PollVote[] = [];

    this.onVote = this.onVote.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.addSelection = this.addSelection.bind(this);
    this.removeSelection = this.removeSelection.bind(this);
    this.onConfirmSelection = this.onConfirmSelection.bind(this);
    this.hasVotes = this.hasVotes.bind(this);
    this.isSlideVoted = this.isSlideVoted.bind(this);

    if (!props.preview && props.interaction.id) {
      votes =
        (LocalStorageService.getVotes(props.interaction.id) as PollVote[]) ||
        votes;
    }

    this.state = {
      votes: votes,
      selection: [],
      onVote: this.onVote,
      onSelect: this.onSelect,
      onConfirmSelection: this.onConfirmSelection,
      hasVotes: this.hasVotes,
      isPreview: props.preview,
      isSlideVoted: this.isSlideVoted,
    };
    this.api = new InteractionService(getApiUrl(props.preview));
  }

  public render(): JSX.Element {
    return (
      <PollContext.Provider value={{ ...this.state }}>
        {this.props.children}
      </PollContext.Provider>
    );
  }

  private onVote(vote: PollVote): void {
    const { isPreview, votes } = this.state;
    const mergedVotes = [...votes, vote];

    if (!isPreview) {
      this.sendVote(vote);
      LocalStorageService.setVotes(vote.interactionId, mergedVotes);
      TrackingService.getInstance().trackOnVote();
    }

    this.setState({ votes: mergedVotes });
  }

  private addSelection(vote: PollVote): void {
    this.setState({ selection: [...this.state.selection, vote] });
  }

  private removeSelection(vote: PollVote): void {
    const selection = this.state.selection.filter(
      (selectedVote: PollVote) => selectedVote.optionId !== vote.optionId
    );
    this.setState({ selection });
  }

  private onSelect(vote: PollVote): void {
    const found = this.state.selection.find(
      (selectedVote: PollVote) => selectedVote.optionId === vote.optionId
    );

    if (found) {
      this.removeSelection(vote);
    } else {
      this.addSelection(vote);
    }
  }

  private onConfirmSelection(): void {
    const { selection, votes, isPreview } = this.state;
    if (!selection.length) {
      return;
    }
    const mergedVotes = [...votes, ...selection];

    if (!isPreview) {
      this.sendVotes(selection);
      LocalStorageService.setVotes(selection[0].interactionId, mergedVotes);
      TrackingService.getInstance().trackOnVote();
    }

    this.setState({ selection: [], votes: [...votes, ...mergedVotes] });
  }

  private hasVotes(): boolean {
    return this.state.votes && this.state.votes.length > 0;
  }

  private isSlideVoted(slideId: string): boolean {
    if (!this.state.votes) {
      return false;
    }
    return !!this.state.votes.find(
      (knownVote: PollVote) => knownVote.slideId === slideId
    );
  }

  private sendVote(vote: PollVote): void {
    this.api.postPollResult(
      vote.interactionId,
      vote.slideId,
      vote.optionId,
      !this.hasVotes()
    );
  }

  private sendVotes(votes: PollVote[]): void {
    if (!votes.length) {
      return;
    }
    const options: PollResult[] = votes.map((vote: PollVote) => ({
      ...vote,
      value: 1,
    }));
    this.api.postPollResults(votes[0].interactionId, options, !this.hasVotes());
  }
}

export const PollConsumer = PollContext.Consumer;
export default PollContext;

/**
 * Poll Voting Utils
 *
 * @implements getPollVotesCount
 * @implements getPollTotalVotes
 * @implements getPollSympathizersCount
 */
export function getPollVotesCount(results: PollResult[]): number {
  return results.reduce(
    (sum: number, result: PollResult) => sum + result.value,
    0
  );
}

export function getPollTotalVotes(results: PollResult[]): number {
  return results
    .map((result: PollResult) => result.value)
    .reduce((a: number, b: number): number => a + b, 0);
}

export function getPollOptionVotesPercent(
  slideResults: PollResult[],
  optionId: string,
  localStorageParticipated = false,
  localStorageIsUserAnswer = false
): number {
  const optionResults: PollResult[] = slideResults.filter(
    (result: PollResult) => result.optionId === optionId
  );
  const optionResult: PollResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;
  let optionVotes: number = optionResult ? optionResult.value : 0;
  let slideVotes = getPollVotesCount(slideResults);

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    optionVotes += 1;
  }
  if (localStorageParticipated) {
    slideVotes += 1;
  }

  return Math.round((optionVotes / slideVotes) * 100);
}

export function getPollOptionVotes(
  slideResults: PollResult[],
  optionId: string,
  localStorageIsUserAnswer = false
): number {
  const optionResults: PollResult[] = slideResults.filter(
    (result: PollResult) => result.optionId === optionId
  );
  const optionResult: PollResult | undefined =
    optionResults.length > 0 ? optionResults[0] : undefined;

  let optionVotes: number = optionResult ? optionResult.value : 0;

  // Maybe a counting problem:
  // Assume: If you are the first participant, the results are empty.
  // If you click on an option, you must see "100% of the participants pick your answer"
  // Problem:
  // - you come back or reload the page and
  // - the caching time is expired and
  // - your answer is now in the official results response
  // => then your answer will be counted twice and the percentage is wrong
  if (localStorageIsUserAnswer) {
    optionVotes += 1;
  }

  return optionVotes;
}

export function getPollSympathizersCount(
  results: PollResult[],
  votes: PollVote[]
): number {
  return results
    .filter((result: PollResult) =>
      votes.find((vote: PollVote) => vote.optionId === result.optionId)
    )
    .map((result: PollResult) => result.value)
    .reduce((a: number, b: number): number => a + b, 0);
}
